import z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";

import {
  Button,
  Group,
  Modal,
  Stack,
  Textarea,
  TextInput
} from "@mantine/core";

import SelectProjectModal from "modules/core/components/SelectProjectModal";

import { useCreateBrainstormIdeaMutation } from "modules/collaboration/hooks/mutations/useCreateBrainstormIdeaMutation";
import { useUpdateBrainstormIdeaMutation } from "modules/collaboration/hooks/mutations/useUpdateBrainstormIdeaMutation";
import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";
import { useActiveIdea } from "../../useActiveIdea";

import { BrainstormIdea } from "modules/collaboration/types/entities";

type BrainstormIdeaModalProps = {
  idea?: Partial<BrainstormIdea>;
  title?: string;
  source?: "manual" | "chat";
  onClose: () => void;
  onSuccess?: (idea: BrainstormIdea) => void;
};

const schema = z.object({
  title: z
    .string()
    .min(10, "Title must have at least 10 characters")
    .max(255, "Title can have max 255 characters"),
  description: z
    .string()
    .max(500, "Description can have max 500 characters")
    .optional()
});

const BrainstormIdeaModal = (props: BrainstormIdeaModalProps) => {
  const { idea, title, source, onClose, onSuccess } = props;

  const { selectedProject, isSingleProject } = useSelectedProject();
  const { setActiveIdea } = useActiveIdea();

  const [projectId, setProjectId] = useState<string | null>(() => {
    if (idea?.project_id) return idea.project_id;
    if (isSingleProject) return selectedProject?.id ?? null;
    return null;
  });

  const { mutate: createBrainstormIdea, isLoading: isCreatingBrainstormIdea } =
    useCreateBrainstormIdeaMutation();
  const { mutate: updateBrainstormIdea, isLoading: isUpdatingBrainstormIdea } =
    useUpdateBrainstormIdeaMutation();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      title: idea?.title || "",
      description: idea?.description || ""
    }
  });

  const submitHandler = handleSubmit(data => {
    if (!projectId) return;

    if (idea?.created_at && idea?.id) {
      updateBrainstormIdea(
        { id: idea.id, dto: data },
        {
          onSuccess: createdIdea => {
            onClose();
            onSuccess?.(createdIdea);
          }
        }
      );
    } else {
      createBrainstormIdea(
        { ...data, project_id: projectId, id: idea?.id, source },
        {
          onSuccess: createdIdea => {
            onClose();
            setActiveIdea(createdIdea.id);
            onSuccess?.(createdIdea);
          }
        }
      );
    }
  });

  if (!projectId)
    return (
      <SelectProjectModal
        isOpen
        onClose={onClose}
        abilitySubject="brainstorming_idea"
        permissionAction="create"
        onSuccess={setProjectId}
      />
    );

  return (
    <Modal
      opened
      title={title || `${idea?.id ? "Edit" : "New"} Brainstorm Idea`}
      onClose={onClose}
    >
      <form noValidate onSubmit={submitHandler}>
        <Stack>
          <TextInput
            {...register("title")}
            label="Title"
            placeholder="Enter title..."
            required
            autoFocus
            data-autofocus
            error={errors.title?.message}
          />

          <Textarea
            {...register("description")}
            label="Description"
            placeholder="Enter description..."
            error={errors.description?.message}
            rows={4}
          />

          <Group justify="flex-end" gap="xs">
            <Button
              variant="outline"
              color="gray"
              c="dark"
              size="sm"
              disabled={isCreatingBrainstormIdea || isUpdatingBrainstormIdea}
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              size="sm"
              loading={isCreatingBrainstormIdea || isUpdatingBrainstormIdea}
            >
              Save Idea
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default BrainstormIdeaModal;
